import { makeAutoObservable, runInAction } from 'mobx'
import {
  type Integration,
  type ISalesForceSettingsDict,
  type ISettingRequestBySettingDict,
} from 'entities/Integrations'
import { type IntegrationSalesforceSettingsInstallationStore } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationSalesforceSettings/store/IntegrationSalesforceSettingsInstallationStore'
import { SALESFORCE_SYNC_CHECKBOX_KEYS } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationSalesforceSettings/lib'
import type { IntegrationCustomSettingsStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationCustomSettingsStore'
import {
  IntegrationSyncType,
  GET_SYNC_TYPE_DICT,
  GET_SYNC_TYPE_OPTIONS,
  INIT_POLL_INTERVAL_MS,
  INIT_POLL_MAX_ATTEMPTS,
} from 'pages/settings/pages/integrations/pages/integrationSettings/lib'

export class IntegrationSalesforceSettingsSyncStore {
  syncType: IntegrationSyncType = IntegrationSyncType.oneWay
  private _initInterval: ReturnType<typeof setInterval> | null = null

  constructor(
    private _integration: Integration,
    private _customSetting: IntegrationCustomSettingsStore<ISalesForceSettingsDict>,
    private _installationStore: IntegrationSalesforceSettingsInstallationStore
  ) {
    makeAutoObservable(this)
  }

  init() {
    this._clearInitInterval()

    let attempts = 0
    this._initInterval = setInterval(() => {
      attempts += 1

      if (this._customSetting.status.isSuccess) {
        this._clearInitInterval()

        if (
          SALESFORCE_SYNC_CHECKBOX_KEYS.some((key) => Boolean(this._customSetting.getValue(key)))
        ) {
          runInAction(() => {
            this.syncType = IntegrationSyncType.twoWay
          })
        }

        return
      }

      if (attempts >= INIT_POLL_MAX_ATTEMPTS) this._clearInitInterval()
    }, INIT_POLL_INTERVAL_MS)
  }

  private _clearInitInterval = () => {
    if (this._initInterval) {
      clearInterval(this._initInterval)
      this._initInterval = null
    }
  }

  dispose = () => {
    this._clearInitInterval()
  }

  updateSyncData = (data: ISettingRequestBySettingDict<ISalesForceSettingsDict>) => {
    this._customSetting.patchData(data)
  }

  setSyncType = (type: IntegrationSyncType) => {
    this.syncType = type

    if (
      type === IntegrationSyncType.oneWay &&
      SALESFORCE_SYNC_CHECKBOX_KEYS.some((key) => Boolean(this._customSetting.getValue(key)))
    ) {
      this.updateSyncData([
        {
          key: 'update_contacts',
          value: 0,
        },
        {
          key: 'create_non_existing_contacts',
          value: 0,
        },
      ])
    }
  }

  setCheckbox = (key: keyof ISalesForceSettingsDict) => (value: boolean) => {
    const data = [
      { key, value: value ? 1 : 0 },
    ] as ISettingRequestBySettingDict<ISalesForceSettingsDict>
    this.updateSyncData(data)
  }

  getCheckbox = (type: keyof ISalesForceSettingsDict) => {
    return Boolean(this._customSetting.getValue(type))
  }

  getValue = (type: keyof ISalesForceSettingsDict) => {
    return this._customSetting.getValue(type)
  }

  setValue =
    (key: keyof ISalesForceSettingsDict) =>
    (value: ISalesForceSettingsDict[keyof ISalesForceSettingsDict]) => {
      const data = [{ key, value }] as ISettingRequestBySettingDict<ISalesForceSettingsDict>
      this.updateSyncData(data)
    }

  getSelectItems = (type: keyof ISalesForceSettingsDict) => {
    return (
      this._customSetting.getOptions(type)?.map((item) => ({
        label: item.label as string,
        id: item.value as string,
        value: item.value as string,
      })) || []
    )
  }

  get isTwoWaySync() {
    return this.syncType === IntegrationSyncType.twoWay
  }

  get getLabel() {
    return GET_SYNC_TYPE_DICT(this._integration.name)[this.syncType]
  }

  get selectItems() {
    return GET_SYNC_TYPE_OPTIONS(this._integration.name)
  }

  get integrationName() {
    return this._integration.name
  }
}
